import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { getToken } from '../../auth/auth.service';

export const PRELOAD_WHEN_IDLE = 'preloadWhenIdle';

const IDLE_DEADLINE_MS = 5000;

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

@Injectable({ providedIn: 'root' })
export class IdlePreloadingStrategy implements PreloadingStrategy {
  preload(
    route: Route,
    loadRoute: () => Observable<unknown>,
  ): Observable<unknown> {
    if (!this.isMarkedForIdlePreload(route)) return EMPTY;
    if (!getToken()) return EMPTY;
    if (this.isConnectionConstrained()) return EMPTY;

    return this.whenBrowserIsIdle().pipe(switchMap(() => loadRoute()));
  }

  private isMarkedForIdlePreload(route: Route): boolean {
    return route.data?.[PRELOAD_WHEN_IDLE] === true;
  }

  private isConnectionConstrained(): boolean {
    const connection: NetworkInformation | undefined = (navigator as any)
      .connection;
    if (!connection) return false;
    return (
      connection.saveData === true ||
      connection.effectiveType === '2g' ||
      connection.effectiveType === 'slow-2g'
    );
  }

  private whenBrowserIsIdle(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const emitOnce = () => {
        subscriber.next();
        subscriber.complete();
      };

      if (typeof requestIdleCallback !== 'function') {
        // Safari still ships no requestIdleCallback; a timer well past first
        // paint is the closest stand-in.
        const timeoutHandle = setTimeout(emitOnce, IDLE_DEADLINE_MS);
        return () => clearTimeout(timeoutHandle);
      }

      const idleHandle = requestIdleCallback(emitOnce, {
        timeout: IDLE_DEADLINE_MS,
      });
      return () => cancelIdleCallback(idleHandle);
    });
  }
}
